/* eslint-disable functional/no-classes -- Using React Native Gesture Handler's class-based API which requires extending Swipeable.SwipeableRow */
import { TEST_ID_WATCHLIST } from "e2e/testId";
import {
  CheckIcon,
  CopyIcon,
  ListIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from "lucide-react-native";
import React, { createRef, PropsWithChildren, PureComponent } from "react";
// design-system: rn-primitive waiver — dynamic style / behavior-coupled
import {
  Animated,
  I18nManager,
  Platform,
  // eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: class-based PureComponent extending react-native-gesture-handler Swipeable; uses Animated.Value interpolation + StyleSheet +…
  Pressable,
  StyleSheet,
  // eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: class-based PureComponent extending react-native-gesture-handler Swipeable; uses Animated.Value interpolation + StyleSheet +…
  Text,
  // eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: class-based PureComponent extending react-native-gesture-handler Swipeable; uses Animated.Value interpolation + StyleSheet +…
  View,
  type ViewStyle,
} from "react-native";
import { Swipeable, SwipeableProps } from "react-native-gesture-handler";

// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: class-based PureComponent extending react-native-gesture-handler Swipeable; uses Animated.Value interpolation + StyleSheet +…
import { Icon } from "@/components/ui/icon";

// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: class-based PureComponent extending react-native-gesture-handler Swipeable; uses Animated.Value interpolation + StyleSheet +…
import { Button, ButtonIcon } from "@/components/ui/button";
import { SwipeableContext } from "@/providers/Swipeable";
import { RADIUS } from "@/config/radius";
import { SEMANTIC_COLORS } from "@/config/semanticColors";

/**
 * Animated translate-X transform for a swipe action.
 * design-system: dynamic-style waiver — animated value (gesture interpolation)
 * @param trans - Animated translateX interpolation
 */
const transformStyle = (trans: Animated.AnimatedInterpolation<number>) => ({
  transform: [{ translateX: trans }],
});

/**
 * Action icon color style.
 * design-system: dynamic-style waiver — data-driven value (per-action icon color)
 * @param color - Resolved icon color
 */
const iconColorStyle = (color: string) => ({ color });

/** Full-size compare button style (web). */
const FULL_SIZE_BUTTON_STYLE = {
  width: "100%" as const,
  height: "100%" as const,
  paddingHorizontal: 30,
};

/**
 * Left actions container style.
 * design-system: dynamic-style waiver — data-driven value (add-affordance width + RTL)
 * @param hasAdd - Whether the add action is present
 * @param isRTL - Whether the layout is right-to-left
 */
const leftActionsStyle = (hasAdd: boolean, isRTL: boolean): ViewStyle => ({
  width: hasAdd ? 44 : 0,
  flexDirection: isRTL ? "row-reverse" : "row",
  alignItems: "center",
  display: hasAdd ? "flex" : "none",
});

/**
 * Right actions container style.
 * design-system: dynamic-style waiver — data-driven value (measured actions width + RTL)
 * @param width - Measured total actions width
 * @param isRTL - Whether the layout is right-to-left
 */
const rightActionsStyle = (width: number, isRTL: boolean): ViewStyle => ({
  width,
  flexDirection: isRTL ? "row-reverse" : "row",
  justifyContent: "flex-end",
});

/**
 * Props for AppleStyleSwipeableRow component with swipe action handlers
 */
type AppleStyleSwipeableRowProps = PropsWithChildren<{
  /** Optional callback invoked when delete action is triggered */
  onDelete?: (() => void) | null;
  /** Optional callback invoked when search/similar action is triggered */
  onSearch?: (() => void) | null;
  /** Optional callback invoked when add to watchlist action is triggered */
  onAdd?: (() => void) | null;
  /** Optional callback invoked when swipeable opens */
  onCallbackOpen?: () => void;
  /** Optional callback invoked when swipeable closes */
  onCallbackClose?: () => void;
  /** Optional callback invoked when compare action is triggered */
  onCompare?: (() => void) | null;
  /** Optional callback invoked when manage lists action is triggered */
  onManageLists?: (() => void) | null;
  /** Optional flag indicating if player is already in any watchlist */
  isPlayerInAnyWatchlist?: boolean;
  /** Optional callback invoked when swipe drag starts with direction and swipeable ref */
  onSwipeableOpenStartDrag?: (
    direction: "left" | "right",
    swipeable: Swipeable | null
  ) => void;
}>;

/** Maps action identifiers to their display labels */
const ACTION_LABELS: Record<string, string> = {
  Search: "Similar Player",
  Delete: "Drop",
  ManageLists: "Lists",
} as const;

/**
 * Returns the display label for a swipe action
 * @param text - The action identifier
 */
const getActionLabel = (text: string): string => ACTION_LABELS[text] ?? text;

/** Maps swipe action identifiers to their right-action icon component. */
const RIGHT_ACTION_ICONS: Record<string, typeof ListIcon> = {
  ManageLists: ListIcon,
  Search: SearchIcon,
  Compare: CopyIcon,
};

/**
 * Returns the icon component for a right-action key, defaulting to TrashIcon.
 * @param text - The action identifier
 */
const getRightActionIcon = (text: string): typeof ListIcon =>
  RIGHT_ACTION_ICONS[text] ?? TrashIcon;

/** Maps right-action keys to their mobile testID, empty string when none. */
const RIGHT_ACTION_TEST_IDS: Record<string, string> = {
  Search: TEST_ID_WATCHLIST.MOBILE_SIMILAR_BUTTON,
  Compare: TEST_ID_WATCHLIST.MOBILE_COMPARE_BUTTON,
};

/**
 * Returns the testID for a right-action key, empty string when none.
 * @param text - The action identifier
 */
const getRightActionTestId = (text: string): string =>
  RIGHT_ACTION_TEST_IDS[text] ?? "";

export default class AppleStyleSwipeableRow extends PureComponent<
  AppleStyleSwipeableRowProps & Omit<SwipeableProps, "onSwipeableOpenStartDrag">
> {
  private readonly handleLeftActionPress = () => {
    this.props.onAdd?.();
    this.close();
  };

  private readonly renderLeftAction = (
    text: string,
    color: string,
    colorIcon: string,
    x: number,
    progress: Animated.AnimatedInterpolation<number>
  ) => {
    const trans = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [-x, 0],
    });

    return (
      <Animated.View
        // eslint-disable-next-line no-restricted-syntax -- Animated translateX interpolation; non-atomizable
        style={transformStyle(trans)}
      >
        <Pressable
          // eslint-disable-next-line no-restricted-syntax -- raw gesture-tree Pressable; data-driven backgroundColor hex + StyleSheet
          style={[styles.leftAction, { backgroundColor: color }]}
          onPress={this.handleLeftActionPress}
        >
          <Icon
            as={text === "Add" ? PlusIcon : CheckIcon}
            size="md"
            // eslint-disable-next-line no-restricted-syntax -- @/components/ui Icon; data-driven icon color, no closed token
            style={iconColorStyle(colorIcon)}
            // @ts-expect-error -- the library is mis-typed
            strokeWidth={3}
          />
        </Pressable>
      </Animated.View>
    );
  };

  private readonly handleSearchPress = () => {
    this.props.onSearch?.();
    this.close();
  };

  private readonly handleComparePress = () => {
    this.props.onCompare?.();
    this.close();
  };

  private readonly handleManageListsPress = () => {
    this.props.onManageLists?.();
    this.close();
  };

  private readonly handleDeletePress = () => {
    this.props.onDelete?.();
    this.close();
  };

  private readonly renderRightAction = (
    text: string,
    color: string,
    x: number,
    progress: Animated.AnimatedInterpolation<number>,
    onPress: () => void
  ) => {
    const trans = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [x, 0],
    });

    return (
      <Animated.View
        // eslint-disable-next-line no-restricted-syntax -- Animated translateX interpolation; non-atomizable
        style={transformStyle(trans)}
      >
        {text === "Compare" && Platform.OS === "web" ? (
          <View
            // eslint-disable-next-line no-restricted-syntax -- raw gesture-tree View; data-driven backgroundColor hex
            style={[
              styles.rightAction,
              { backgroundColor: color, paddingHorizontal: 0 },
            ]}
          >
            <Button
              testID={TEST_ID_WATCHLIST.MOBILE_COMPARE_BUTTON}
              onPress={onPress}
              // eslint-disable-next-line no-restricted-syntax -- @/components/ui compound Button; percentage sizing, non-collapsible composition
              style={FULL_SIZE_BUTTON_STYLE}
              variant="link"
            >
              <ButtonIcon
                as={CopyIcon}
                width={24}
                height={24}
                className="text-content-on-accent"
              />
            </Button>
          </View>
        ) : (
          <Pressable
            testID={getRightActionTestId(text)}
            // eslint-disable-next-line no-restricted-syntax -- raw gesture-tree Pressable action row
            style={styles.rightAction}
            onPress={onPress}
          >
            <View
              // eslint-disable-next-line no-restricted-syntax -- raw gesture-tree View; data-driven backgroundColor hex, 40px circle
              style={[styles.rightActionCircle, { backgroundColor: color }]}
            >
              <Icon as={getRightActionIcon(text)} size="lg" />
            </View>
            <Text
              // eslint-disable-next-line no-restricted-syntax -- raw gesture-tree Text; fontSize 11/lineHeight 13 has no text variant
              style={styles.rightActionLabel}
            >
              {getActionLabel(text)}
            </Text>
          </Pressable>
        )}
      </Animated.View>
    );
  };

  private readonly renderLeftActions = (
    progress: Animated.AnimatedInterpolation<number>,
    _dragAnimatedValue: Animated.AnimatedInterpolation<number>
  ) => (
    <View
      // eslint-disable-next-line no-restricted-syntax -- data-driven width + RTL flexDirection + display toggle
      style={leftActionsStyle(Boolean(this.props.onAdd), I18nManager.isRTL)}
    >
      {this.renderLeftAction(
        this.props?.isPlayerInAnyWatchlist ? "Check" : "Add",
        this.props?.isPlayerInAnyWatchlist ? "#0b8dcd" : "#26252A",
        this.props?.isPlayerInAnyWatchlist
          ? SEMANTIC_COLORS["content-on-accent"]
          : "#0b8dcd",
        60,
        progress
      )}
    </View>
  );

  private readonly renderRightActions = (
    progress: Animated.AnimatedInterpolation<number>,
    _dragAnimatedValue: Animated.AnimatedInterpolation<number>
  ) => (
    <View
      // eslint-disable-next-line no-restricted-syntax -- runtime-measured actions width + RTL flexDirection
      style={rightActionsStyle(this.getActionsWidth(), I18nManager.isRTL)}
    >
      {this.props.onManageLists
        ? this.renderRightAction(
            "ManageLists",
            "#4CAF50",
            228,
            progress,
            this.handleManageListsPress
          )
        : null}
      {this.props.onSearch
        ? this.renderRightAction(
            "Search",
            "#0b8dcd",
            152,
            progress,
            this.handleSearchPress
          )
        : null}
      {this.props.onCompare
        ? this.renderRightAction(
            "Compare",
            "#4CAF50",
            240,
            progress,
            this.handleComparePress
          )
        : null}
      {this.props.onDelete
        ? this.renderRightAction(
            "Delete",
            "#dd2c00",
            76,
            progress,
            this.handleDeletePress
          )
        : null}
    </View>
  );

  private swipeableRow = createRef<Swipeable>();
  static readonly contextType = SwipeableContext; // use context in a class component

  private readonly close = () => {
    const { setOpen } = this.context as {
      setOpen: (ref: React.RefObject<Swipeable | null> | undefined) => void;
    };
    setOpen(undefined);
  };

  private readonly getActionsWidth = () => {
    const count = [
      this.props.onManageLists,
      this.props.onSearch,
      this.props.onCompare,
      this.props.onDelete,
    ].filter(Boolean).length;
    return count <= 2 ? count * 100 : count * 76;
  };

  private handleSwipeableWillOpen = () => {
    // "worklet";
    // setOpen is undefined when called inside a Reanimated "worklet" on Web.
    this.props?.onCallbackOpen?.();
    const { setOpen } = this.context as {
      setOpen: (ref: React.RefObject<Swipeable | null> | undefined) => void;
    };
    setOpen(this.swipeableRow);
  };
  private handleSwipeableWillClose = () => {
    this.props?.onCallbackClose?.();
  };

  private handleSwipeableOpenStartDrag = (direction: "left" | "right") => {
    this.props.onSwipeableOpenStartDrag?.(direction, this.swipeableRow.current);
  };

  render() {
    const {
      children,
      onSwipeableOpenStartDrag: _onSwipeableOpenStartDrag,
      ...swipeableProps
    } = this.props;
    return (
      <Swipeable
        ref={this.swipeableRow}
        friction={2}
        enableTrackpadTwoFingerGesture
        overshootRight={false}
        overshootLeft={false}
        leftThreshold={40}
        rightThreshold={40}
        // Gesture arbitration (SE-4611): the row's horizontal swipe pan must
        // yield to the list's vertical scroll. activeOffsetX requires a clearly
        // horizontal drag before the swipe activates; failOffsetY makes the pan
        // give up once the finger moves vertically — so swiping a card never
        // "fights the finger" during momentum scroll. The legacy Swipeable
        // forwards both to its PanGestureHandler. Applies to every swipeable
        // list row (Scouting targets, Watchlist, Search, Notes, etc.).
        activeOffsetX={[-10, 10]}
        failOffsetY={[-10, 10]}
        renderLeftActions={this.renderLeftActions}
        renderRightActions={this.renderRightActions}
        onSwipeableWillOpen={this.handleSwipeableWillOpen}
        onSwipeableClose={this.handleSwipeableWillClose}
        onSwipeableOpenStartDrag={this.handleSwipeableOpenStartDrag}
        {...swipeableProps}
      >
        {children}
      </Swipeable>
    );
  }
}

const styles = StyleSheet.create({
  leftAction: {
    alignItems: "center",
    padding: 4,
    justifyContent: "center",
    borderRadius: RADIUS.xl,
  },
  rightAction: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    paddingHorizontal: 10,
    // SE-5446 follow-up: the action column (circle + label) must fit a compact
    // list row (~58dp for recommendation cards) or the label clips. Tight gap +
    // a 40dp circle + a capped label line-height keep it under the row height.
    gap: 2,
  },
  rightActionCircle: {
    width: 40,
    height: 40,
    borderRadius: RADIUS.xl,
    alignItems: "center",
    justifyContent: "center",
  },
  rightActionLabel: {
    color: "#ffffff",
    fontSize: 11,
    lineHeight: 13,
    textAlign: "center",
  },
});
/* eslint-enable functional/no-classes -- Re-enable after class definition */
